import { EVENT_STATUS } from './event.types';
import { UserRole } from 'app/core/user/user.types';

/**
 * LION-620 (ARCH-01): grafo de transiciones del workflow de órdenes y helpers
 * puros. `FALLBACK_TRANSITIONS` espeja la matriz canónica del back; se usa
 * mientras la matriz remota (GET /events/status-transitions) no resolvió, o si
 * ese GET falla. El back sigue siendo la AUTORIDAD de validación: este grafo
 * sólo gobierna habilitación/presentación en el front.
 */
export type TransitionMatrix = Partial<Record<EVENT_STATUS, EVENT_STATUS[]>>;

export const FALLBACK_TRANSITIONS: TransitionMatrix = {
  [EVENT_STATUS.OPEN]: [EVENT_STATUS.RECEIVED],
  [EVENT_STATUS.RECEIVED]: [EVENT_STATUS.BUDGETED],
  [EVENT_STATUS.BUDGETED]: [
    EVENT_STATUS.APPROVED,
    EVENT_STATUS.PARTIAL_APPROVAL,
    EVENT_STATUS.REJECTED,
    EVENT_STATUS.PARTIAL_REJECTION,
  ],
  [EVENT_STATUS.PARTIAL_APPROVAL]: [
    EVENT_STATUS.APPROVED,
    EVENT_STATUS.REJECTED,
    EVENT_STATUS.PARTIAL_REJECTION,
  ],
  [EVENT_STATUS.APPROVED]: [EVENT_STATUS.IN_PROGRESS, EVENT_STATUS.REJECTED],
  [EVENT_STATUS.PARTIAL_REJECTION]: [EVENT_STATUS.BUDGETED],
  // LION-533: REJECTED → DELIVERED se eliminó — el status de un evento
  // rechazado nunca cambia a DELIVERED (ver EventsService.registerRejectedVehicleExit
  // en el back). La salida del vehículo se registra sin tocar el status.
  [EVENT_STATUS.REJECTED]: [EVENT_STATUS.BUDGETED],
  [EVENT_STATUS.IN_PROGRESS]: [EVENT_STATUS.COMPLETED],
  [EVENT_STATUS.COMPLETED]: [EVENT_STATUS.DELIVERED],
};

export const getAllowedTransitions = (
  from: EVENT_STATUS,
  matrix: TransitionMatrix = FALLBACK_TRANSITIONS,
): EVENT_STATUS[] => matrix[from] ?? [];

export const canTransition = (
  from: EVENT_STATUS,
  to: EVENT_STATUS,
  matrix: TransitionMatrix = FALLBACK_TRANSITIONS,
): boolean => getAllowedTransitions(from, matrix).includes(to);

// ── getNextAction: réplica EXACTA de event.component.nextStatusAction:519-590 ──

export interface NextActionContext {
  /** form.get('status').value */
  status: EVENT_STATUS;
  /** rol del usuario actual (currentUser?.role) */
  role: UserRole | undefined;
  /** true si es cliente (this.isClientUser) — corta a null si true */
  isClientUser: boolean;
  /**
   * `event.component.pvpOperatesEvent`: la empresa delega la operación del evento
   * en PVP (`company.allowPvpBudgetLoad`) y el rol del usuario es uno de los que
   * la operan. Única excepción al corte por `isClientUser` — ver `getNextAction`.
   */
  pvpOperatesEvent?: boolean;
  /** algún item del FormArray tiene _id persistido (RECEIVED/REJECTED) */
  hasPersistedItems: boolean;
  /** raw.workshopStartDate (APPROVED) */
  workshopStartDate: string | null | undefined;
  /** raw.workshopEndDate (IN_PROGRESS) */
  workshopEndDate: string | null | undefined;
  /** raw.vehicleExitDate (COMPLETED) */
  vehicleExitDate: string | null | undefined;
}

export interface NextStatusAction {
  label: string;
  nextStatus: EVENT_STATUS;
  enabled: boolean;
  tooltip: string;
}

export const getNextAction = (ctx: NextActionContext): NextStatusAction | null => {
  // El usuario cliente no dispara transiciones… con UNA excepción: cuando la
  // empresa delega la operación del evento en PVP (`allowPvpBudgetLoad`), quien
  // mueve el evento es PVP desde el detalle, así que necesita esos botones.
  //
  // Cubre el circuito operativo COMPLETO —ingreso, presupuestada, en progreso,
  // completada, entregada—: si PVP opera el evento, lo opera de punta a punta.
  //
  // Ojo con el nombre `isClientUser`: significa "tiene empresas asignadas", no "es
  // del cliente". Los SUPER_ADMIN de PVP TIENEN empresas asignadas —
  // `getUserApprovalLevel` lo exige para que puedan aprobar—, así que también caen
  // en este corte: sin la excepción, un SUPER_ADMIN se quedaba sin ningún botón de
  // avance de estado en el detalle.
  //
  // Queda afuera REJECTED → BUDGETED ("Volver a Presupuestar"): el back la reserva
  // a nivel de aprobación PVP (`EventsService.updateStatus`), así que para un ADMIN
  // de empresa sería un 403. El caso OPEN además sigue exigiendo rol admin más
  // abajo, así que un USER cliente no ve nada.
  if (ctx.isClientUser) {
    if (!ctx.pvpOperatesEvent) return null;
    if (ctx.status === EVENT_STATUS.REJECTED) return null;
  }
  switch (ctx.status) {
    case EVENT_STATUS.OPEN: {
      const isAdmin =
        ctx.role === UserRole.ADMIN ||
        ctx.role === UserRole.SUPER_ADMIN ||
        ctx.role === UserRole.MASTER;
      if (!isAdmin) return null;
      return { label: 'Marcar Ingreso', nextStatus: EVENT_STATUS.RECEIVED, enabled: true, tooltip: '' };
    }
    case EVENT_STATUS.RECEIVED:
      return {
        label: 'Marcar Presupuestada', nextStatus: EVENT_STATUS.BUDGETED,
        enabled: ctx.hasPersistedItems,
        tooltip: ctx.hasPersistedItems ? '' : 'Debe guardar al menos un ítem antes de presupuestar',
      };
    case EVENT_STATUS.APPROVED:
      return {
        label: 'Marcar En Progreso', nextStatus: EVENT_STATUS.IN_PROGRESS,
        enabled: !!ctx.workshopStartDate,
        tooltip: ctx.workshopStartDate ? '' : 'Debe cargar la fecha de inicio de mano de obra',
      };
    case EVENT_STATUS.IN_PROGRESS:
      return {
        label: 'Marcar Completada', nextStatus: EVENT_STATUS.COMPLETED,
        enabled: !!ctx.workshopEndDate,
        tooltip: ctx.workshopEndDate ? '' : 'Debe cargar la fecha de finalización de mano de obra',
      };
    case EVENT_STATUS.COMPLETED:
      return {
        label: 'Marcar Entregada', nextStatus: EVENT_STATUS.DELIVERED,
        enabled: !!ctx.vehicleExitDate,
        tooltip: ctx.vehicleExitDate ? '' : 'Debe cargar la fecha de egreso del vehículo',
      };
    case EVENT_STATUS.REJECTED:
      return {
        label: 'Volver a Presupuestar', nextStatus: EVENT_STATUS.BUDGETED,
        enabled: ctx.hasPersistedItems,
        tooltip: ctx.hasPersistedItems ? '' : 'Debe guardar al menos un ítem antes de presupuestar',
      };
    default:
      return null;
  }
};
